import React, { useState, useRef, useCallback, useEffect, Suspense, Component, ErrorInfo, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

// Error boundary to catch lazy-load failures (stale chunks after deploy)
class ModalErrorBoundary extends Component<{ children: ReactNode; onReset: () => void }, { hasError: boolean }> {
  constructor(props: { children: ReactNode; onReset: () => void }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.warn('[QuickActions] Modal load failed:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '2rem', maxWidth: '400px', textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⚠️</div>
            <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Failed to load</div>
            <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>A newer version is available. Please refresh the page.</p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
              <button onClick={() => { this.setState({ hasError: false }); this.props.onReset(); }} style={{ padding: '8px 16px', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem' }}>Close</button>
              <button onClick={() => window.location.reload()} style={{ padding: '8px 16px', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' }}>Refresh Page</button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const LinkRegistrationModal = React.lazy(() => import('./LinkRegistrationModal').then(m => ({ default: m.LinkRegistrationModal })));
const LinkMemberModal = React.lazy(() => import('./LinkMemberModal').then(m => ({ default: m.LinkMemberModal })));
const AssignTableModal = React.lazy(() => import('./AssignTableModal').then(m => ({ default: m.AssignTableModal })));
const RegisterAttendeeModal = React.lazy(() => import('./RegisterAttendeeModal').then(m => ({ default: m.RegisterAttendeeModal })));
const CreateSubmissionModal = React.lazy(() => import('./CreateSubmissionModal').then(m => ({ default: m.CreateSubmissionModal })));
const CheckInModal = React.lazy(() => import('./CheckInModal').then(m => ({ default: m.CheckInModal })));
const ViewGuestInfoModal = React.lazy(() => import('./ViewGuestInfoModal').then(m => ({ default: m.ViewGuestInfoModal })));
const LegacyBallAttendeesModal = React.lazy(() => import('./LegacyBallAttendeesModal').then(m => ({ default: m.LegacyBallAttendeesModal })));
const ManageGuestAttrsModal = React.lazy(() => import('./ManageGuestAttrsModal').then(m => ({ default: m.ManageGuestAttrsModal })));
const DashboardContentModal = React.lazy(() => import('./DashboardContentModal').then(m => ({ default: m.DashboardContentModal })));
const ArticleReviewModal = React.lazy(() => import('./ArticleReviewModal').then(m => ({ default: m.ArticleReviewModal })));
const RegistrationStatsModal = React.lazy(() => import('./RegistrationStatsModal').then(m => ({ default: m.RegistrationStatsModal })));
const ChatbotHelpModal = React.lazy(() => import('./ChatbotHelpModal').then(m => ({ default: m.ChatbotHelpModal })));
const SystemFlagsModal = React.lazy(() => import('./SystemFlagsModal').then(m => ({ default: m.SystemFlagsModal })));
const EventComparisonModal = React.lazy(() => import('./EventComparisonModal').then(m => ({ default: m.EventComparisonModal })));
const UpdateWelcomeMessageModal = React.lazy(() => import('./UpdateWelcomeMessageModal').then(m => ({ default: m.UpdateWelcomeMessageModal })));
const SendNotificationModal = React.lazy(() => import('./SendNotificationModal').then(m => ({ default: m.SendNotificationModal })));
const AiInsightsModal = React.lazy(() => import('./AiInsightsModal').then(m => ({ default: m.AiInsightsModal })));
const PresenterAccessModal = React.lazy(() => import('./PresenterAccessModal').then(m => ({ default: m.PresenterAccessModal })));
const CommPortalModal = React.lazy(() => import('./CommPortalModal').then(m => ({ default: m.CommPortalModal })));
const ActivateGuestModal = React.lazy(() => import('./ActivateGuestModal').then(m => ({ default: m.ActivateGuestModal })));
const EmailReconciliationModal = React.lazy(() => import('./EmailReconciliationModal').then(m => ({ default: m.EmailReconciliationModal })));
const MemberTicketLinkAuditModal = React.lazy(() => import('./MemberTicketLinkAuditModal').then(m => ({ default: m.MemberTicketLinkAuditModal })));
const VendorGuestMatchModal = React.lazy(() => import('./VendorGuestMatchModal').then(m => ({ default: m.VendorGuestMatchModal })));
const VendorStagedEditsModal = React.lazy(() => import('./VendorStagedEditsModal').then(m => ({ default: m.VendorStagedEditsModal })));

export interface QuickActionConfig {
  id: string;
  icon: string;
  label: string;
  description: string;
  color: string;
  path?: string;
  category: 'legacy-ball' | 'booklet' | 'analytics' | 'operations' | 'communications';
}

const CATEGORIES: { key: string; icon: string; label: string; bgColor: string }[] = [
  { key: 'legacy-ball', icon: '🎭', label: 'Legacy Ball', bgColor: '#e8f5e9' },
  { key: 'booklet', icon: '📖', label: 'Weekend Booklet', bgColor: '#fef3c7' },
  { key: 'analytics', icon: '📊', label: 'Analytics & Insights', bgColor: '#e3f2fd' },
  { key: 'operations', icon: '⚙️', label: 'Operations & System', bgColor: '#eceff1' },
  { key: 'communications', icon: '💬', label: 'Communications', bgColor: '#e1f5fe' },
];

export const QUICK_ACTIONS: QuickActionConfig[] = [
  {
    id: 'register-attendee',
    icon: '🎟️',
    label: 'Register Attendee',
    description: 'Quick-register a user for the Legacy Ball',
    color: '#2e7d32',
    category: 'legacy-ball',
  },
  {
    id: 'assign-table',
    icon: '🪑',
    label: 'Assign Table Seat',
    description: 'Assign a banquet table seat to a guest',
    color: '#e65100',
    category: 'legacy-ball',
  },
  {
    id: 'check-in',
    icon: '✅',
    label: 'Check-In Guest',
    description: 'Quick check-in for the Legacy Ball',
    color: '#00695c',
    category: 'legacy-ball',
  },
  {
    id: 'legacy-ball-attendees',
    icon: '📋',
    label: 'Legacy Ball Attendees',
    description: 'View all registered attendees and their next step',
    color: '#7b1fa2',
    category: 'legacy-ball',
  },
  {
    id: 'manage-guest-attrs',
    icon: '👥',
    label: 'Manage Guest Attributes',
    description: 'Set VIP, family, accessibility for seating engine',
    color: '#0d47a1',
    category: 'legacy-ball',
  },
  {
    id: 'view-guest-info',
    icon: '🔍',
    label: 'View Guest Info',
    description: 'Look up any guest\'s full weekend details',
    color: '#455a64',
    category: 'legacy-ball',
  },
  {
    id: 'create-submission',
    icon: '📝',
    label: 'Create Booklet Submission',
    description: 'Create a new tile, ad, or article submission',
    color: '#c9a84c',
    category: 'booklet',
  },
  {
    id: 'link-registration',
    icon: '🔗',
    label: 'Link Registration to Tile',
    description: 'Link a registration to a booklet tile/ad',
    color: '#1565c0',
    category: 'booklet',
  },
  {
    id: 'link-ad-space',
    icon: '📰',
    label: 'Link Registration to Ad Space',
    description: 'Link a registration to an ad space purchase',
    color: '#0277bd',
    category: 'booklet',
  },
  {
    id: 'article-review',
    icon: '📖',
    label: 'View My Articles for Review',
    description: 'Review assigned booklet articles',
    color: '#6a1b9a',
    category: 'booklet',
  },
  {
    id: 'registration-stats',
    icon: '📊',
    label: 'Registration Stats',
    description: 'View totals: registered, ticketed, seated, checked-in',
    color: '#1565c0',
    category: 'analytics',
  },
  {
    id: 'event-comparison',
    icon: '📊',
    label: 'Event Comparison',
    description: 'Side-by-side stats for all events',
    color: '#0d47a1',
    category: 'analytics',
  },
  {
    id: 'ai-insights',
    icon: '🧠',
    label: 'AI Seating Insights',
    description: 'Zone analysis, chapter clustering, key members',
    color: '#1a6b3c',
    category: 'analytics',
  },
  {
    id: 'dashboard-content',
    icon: '📺',
    label: 'Dashboard Content',
    description: 'Manage fun facts, quotes, and slideshow images',
    color: '#0d47a1',
    category: 'analytics',
  },
  {
    id: 'logistics-center',
    icon: '🏢',
    label: 'K-LOC',
    description: 'KDU-HUB Logistics Operations Center',
    color: '#37474f',
    path: '/logistics-center',
    category: 'operations',
  },
  {
    id: 'link-member',
    icon: '👤',
    label: 'Link Member to User',
    description: 'Create a user account from a KDU member',
    color: '#6a1b9a',
    category: 'operations',
  },
  {
    id: 'chatbot-help',
    icon: '🤖',
    label: 'Chatbot Commands',
    description: 'View all AI concierge chatbot commands and examples',
    color: '#00695c',
    category: 'operations',
  },
  {
    id: 'system-flags',
    icon: '⚙️',
    label: 'System Flags',
    description: 'View all feature toggles and their ON/OFF status',
    color: '#37474f',
    category: 'operations',
  },
  {
    id: 'update-welcome-message',
    icon: '🎁',
    label: 'Update Welcome Message',
    description: 'Edit the Welcome Package card shown to all guests',
    color: '#b45309',
    category: 'operations',
  },
  {
    id: 'food-menu-admin',
    icon: '🍽️',
    label: 'Food Menu Admin',
    description: 'Manage Awujoh food items and vendor offerings',
    color: '#e65100',
    path: '/food-catalog/manage',
    category: 'operations',
  },
  {
    id: 'send-notification',
    icon: '📢',
    label: 'Send Notification',
    description: 'Create and publish an announcement to all guests',
    color: '#7c3aed',
    category: 'operations',
  },
  {
    id: 'qr-generator',
    icon: '🎫',
    label: 'QR Guest Cards',
    description: 'Generate printable QR cards for guest welcome packets',
    color: '#0d47a1',
    path: '/qr-generator',
    category: 'legacy-ball',
  },
  {
    id: 'available-seats',
    icon: '🪑',
    label: 'Available Seats Report',
    description: 'View tables with open seats sorted by availability',
    color: '#1a6b3c',
    path: '/legacy-ball',
    category: 'analytics',
  },
  {
    id: 'presenter-access',
    icon: '🎤',
    label: 'Presenter Access Codes',
    description: 'Manage MC/presenter portal access codes',
    color: '#6a1b9a',
    category: 'operations',
  },
  {
    id: 'activate-guest',
    icon: '🔓',
    label: 'Activate Guest Account',
    description: 'Activate Newly Imported Guests',
    color: '#f57c00',
    category: 'operations',
  },
  {
    id: 'email-reconciliation',
    icon: '📧',
    label: 'Email Reconciliation',
    description: 'Compare & sync Hub vs Member emails',
    color: '#1565c0',
    category: 'operations',
  },
  {
    id: 'member-ticket-audit',
    icon: '🔗',
    label: 'Member-Ticket Link Audit',
    description: 'Audit & fix member-to-ticket linking relationships',
    color: '#7c3aed',
    category: 'operations',
  },
  {
    id: 'vendor-guest-match',
    icon: '🏪',
    label: 'Vendor-Guest Match',
    description: 'Find vendors who are also registered guests and confirm matches',
    color: '#0d9488',
    category: 'operations',
  },
  {
    id: 'vendor-staged-edits',
    icon: '📋',
    label: 'Vendor Edit Requests',
    description: 'Review and approve vendor self-service catalog changes',
    color: '#7c3aed',
    category: 'operations',
  },
  {
    id: 'feature-report',
    icon: '📋',
    label: 'Feature Suitability Report',
    description: 'Print a comprehensive feature report for guest experience evaluation',
    color: '#1a6b3c',
    category: 'operations',
    path: '/feature-report',
  },
  {
    id: 'whatsapp-share',
    icon: '📱',
    label: 'Share to WhatsApp',
    description: 'Compose and send a message to the reunion WhatsApp group',
    color: '#25D366',
    category: 'communications',
  },
  {
    id: 'comm-portal',
    icon: '💬',
    label: 'Communications Portal',
    description: 'Manage discussion categories and access codes',
    color: '#0277bd',
    category: 'communications',
  },
];

export type ActiveModal =
  | null
  | 'link-registration'
  | 'link-member'
  | 'assign-table'
  | 'register-attendee'
  | 'create-submission'
  | 'check-in'
  | 'link-ad-space'
  | 'view-guest-info'
  | 'legacy-ball-attendees'
  | 'manage-guest-attrs'
  | 'dashboard-content'
  | 'article-review'
  | 'registration-stats'
  | 'chatbot-help'
  | 'system-flags'
  | 'event-comparison'
  | 'update-welcome-message'
  | 'send-notification'
  | 'ai-insights'
  | 'presenter-access'
  | 'comm-portal'
  | 'whatsapp-share'
  | 'activate-guest'
  | 'email-reconciliation'
  | 'member-ticket-audit'
  | 'vendor-guest-match'
  | 'vendor-staged-edits';

const RECENTS_KEY = 'kdu-quick-action-recents';
const MAX_RECENTS = 4;

function getRecents(): string[] {
  try {
    const stored = localStorage.getItem(RECENTS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) return parsed.slice(0, MAX_RECENTS);
    }
  } catch { /* ignore */ }
  return [];
}

function addRecent(actionId: string): string[] {
  const recents = getRecents().filter(id => id !== actionId);
  recents.unshift(actionId);
  const updated = recents.slice(0, MAX_RECENTS);
  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(updated));
  } catch { /* ignore */ }
  return updated;
}

export function QuickActionsSection() {
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [recents, setRecents] = useState<string[]>(getRecents);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [searchFocused, setSearchFocused] = useState(false);
  const [whatsappMessage, setWhatsappMessage] = useState('');
  const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const searchRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Keyboard shortcut: Cmd+K / Ctrl+K to focus search
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const handleOpenModal = useCallback((id: ActiveModal) => {
    setActiveModal(id);
  }, []);

  const handleTileClick = useCallback((action: QuickActionConfig) => {
    setRecents(addRecent(action.id));
    if (action.path) {
      navigate(action.path);
    } else {
      handleOpenModal(action.id as ActiveModal);
    }
  }, [navigate, handleOpenModal]);

  const handleClose = useCallback(() => {
    const triggerId = activeModal;
    setActiveModal(null);
    if (triggerId && buttonRefs.current[triggerId]) {
      setTimeout(() => {
        buttonRefs.current[triggerId]?.focus();
      }, 0);
    }
  }, [activeModal]);

  const handleSuccess = useCallback((message: string) => {
    const triggerId = activeModal;
    setActiveModal(null);
    setSuccessMessage(message);
    if (triggerId && buttonRefs.current[triggerId]) {
      setTimeout(() => {
        buttonRefs.current[triggerId]?.focus();
      }, 0);
    }
    setTimeout(() => {
      setSuccessMessage('');
    }, 4000);
  }, [activeModal]);

  // Filter actions by search query
  const filteredActions = searchQuery.trim()
    ? QUICK_ACTIONS.filter(a =>
        a.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : QUICK_ACTIONS;

  // Group filtered actions by category
  const groupedActions = CATEGORIES.map(cat => ({
    ...cat,
    actions: filteredActions.filter(a => a.category === cat.key),
  })).filter(group => group.actions.length > 0);

  // Resolve recent action configs
  const recentActions = recents
    .map(id => QUICK_ACTIONS.find(a => a.id === id))
    .filter((a): a is QuickActionConfig => a !== undefined);

  return (
    <div style={{ marginBottom: '2rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#0f2d1f' }}>⚡ Command Center</div>
          <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '2px' }}>Quick actions organized by workflow area</div>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: '#fff',
            border: searchFocused ? '1px solid #1a6b3c' : '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '8px 14px',
            width: '280px',
            transition: 'border-color 0.2s, box-shadow 0.2s',
            boxShadow: searchFocused ? '0 0 0 2px rgba(26,107,60,0.1)' : 'none',
          }}
        >
          <span style={{ fontSize: '1rem', opacity: 0.5 }}>🔍</span>
          <input
            ref={searchRef}
            type="text"
            placeholder="Search actions... (⌘K)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            style={{ border: 'none', outline: 'none', flex: 1, fontSize: '0.85rem', background: 'transparent' }}
            aria-label="Search quick actions"
          />
        </div>
      </div>

      {/* Recently Used Strip */}
      {recentActions.length > 0 && !searchQuery.trim() && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '1.25rem', paddingBottom: '10px', borderBottom: '1px solid #e5e7eb', overflowX: 'auto' }}>
          {recentActions.map(action => (
            <button
              key={`recent-${action.id}`}
              onClick={() => handleTileClick(action)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '20px',
                fontSize: '0.75rem',
                fontWeight: 500,
                color: '#166534',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#dcfce7';
                e.currentTarget.style.borderColor = '#86efac';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#f0fdf4';
                e.currentTarget.style.borderColor = '#bbf7d0';
              }}
              aria-label={`Recent: ${action.label}`}
            >
              <span style={{ fontSize: '0.85rem' }}>{action.icon}</span>
              {action.label}
            </button>
          ))}
        </div>
      )}

      {/* Categorized Sections */}
      {groupedActions.map(group => (
        <div key={group.key} style={{ marginBottom: '1.5rem' }}>
          {/* Category Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '0.6rem', padding: '0 4px' }}>
            <div style={{
              width: '28px',
              height: '28px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.9rem',
              background: group.bgColor,
            }}>
              {group.icon}
            </div>
            <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#0f2d1f', letterSpacing: '0.02em' }}>{group.label}</span>
            <span style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: 500, marginLeft: '4px' }}>{group.actions.length} action{group.actions.length !== 1 ? 's' : ''}</span>
            <div style={{ flex: 1, height: '1px', background: '#e5e7eb', marginLeft: '8px' }} />
          </div>

          {/* Action Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.6rem' }}>
            {group.actions.map(action => {
              const isHovered = hoveredCard === action.id;
              return (
                <button
                  key={action.id}
                  ref={(el) => { buttonRefs.current[action.id] = el; }}
                  onClick={() => handleTileClick(action)}
                  onMouseEnter={() => setHoveredCard(action.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '12px 14px',
                    background: '#fff',
                    border: isHovered ? `1px solid ${action.color}` : '1px solid #e5e7eb',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    textAlign: 'left',
                    width: '100%',
                    boxShadow: isHovered ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
                    transform: isHovered ? 'translateY(-1px)' : 'none',
                  }}
                  aria-label={`${action.label}: ${action.description}`}
                >
                  {/* Left glow bar */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '3px',
                    height: '100%',
                    background: action.color,
                    opacity: isHovered ? 1 : 0,
                    transition: 'opacity 0.15s',
                    borderRadius: '3px 0 0 3px',
                  }} />
                  {/* Icon */}
                  <div style={{
                    width: '34px',
                    height: '34px',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.1rem',
                    flexShrink: 0,
                    background: `${action.color}18`,
                  }}>
                    {action.icon}
                  </div>
                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#0f2d1f', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {action.label}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: '#6b7280', lineHeight: 1.3, marginTop: '1px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {action.description}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {/* Empty search state */}
      {searchQuery.trim() && groupedActions.length === 0 && (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280', fontSize: '0.85rem' }}>
          No actions match &ldquo;{searchQuery}&rdquo;
        </div>
      )}

      {/* Lazy-loaded modal components */}
      {activeModal && (
        <ModalErrorBoundary onReset={handleClose}>
        <Suspense fallback={null}>
          {activeModal === 'link-registration' && (
            <LinkRegistrationModal onClose={handleClose} onSuccess={handleSuccess} />
          )}
          {activeModal === 'link-member' && (
            <LinkMemberModal onClose={handleClose} onSuccess={handleSuccess} />
          )}
          {activeModal === 'assign-table' && (
            <AssignTableModal onClose={handleClose} onSuccess={handleSuccess} />
          )}
          {activeModal === 'register-attendee' && (
            <RegisterAttendeeModal onClose={handleClose} onSuccess={handleSuccess} />
          )}
          {activeModal === 'create-submission' && (
            <CreateSubmissionModal onClose={handleClose} onSuccess={handleSuccess} />
          )}
          {activeModal === 'check-in' && (
            <CheckInModal onClose={handleClose} onSuccess={handleSuccess} />
          )}
          {activeModal === 'link-ad-space' && (
            <LinkRegistrationModal
              onClose={handleClose}
              onSuccess={handleSuccess}
              categoryFilter="Advertisers"
              title="Link Registration to Ad Space"
            />
          )}
          {activeModal === 'view-guest-info' && (
            <ViewGuestInfoModal onClose={handleClose} />
          )}
          {activeModal === 'legacy-ball-attendees' && (
            <LegacyBallAttendeesModal onClose={handleClose} />
          )}
          {activeModal === 'manage-guest-attrs' && (
            <ManageGuestAttrsModal onClose={handleClose} />
          )}
          {activeModal === 'dashboard-content' && (
            <DashboardContentModal onClose={handleClose} onSuccess={handleSuccess} />
          )}
          {activeModal === 'article-review' && (
            <ArticleReviewModal onClose={handleClose} onSuccess={handleSuccess} />
          )}
          {activeModal === 'registration-stats' && (
            <RegistrationStatsModal onClose={handleClose} />
          )}
          {activeModal === 'chatbot-help' && (
            <ChatbotHelpModal onClose={handleClose} />
          )}
          {activeModal === 'system-flags' && (
            <SystemFlagsModal onClose={handleClose} />
          )}
          {activeModal === 'event-comparison' && (
            <EventComparisonModal onClose={handleClose} />
          )}
          {activeModal === 'update-welcome-message' && (
            <UpdateWelcomeMessageModal onClose={handleClose} onSuccess={handleSuccess} />
          )}
          {activeModal === 'send-notification' && (
            <SendNotificationModal onClose={handleClose} onSuccess={handleSuccess} />
          )}
          {activeModal === 'ai-insights' && (
            <AiInsightsModal onClose={handleClose} />
          )}
          {activeModal === 'presenter-access' && (
            <PresenterAccessModal onClose={handleClose} />
          )}
          {activeModal === 'activate-guest' && (
            <ActivateGuestModal onClose={handleClose} onSuccess={handleSuccess} />
          )}
          {activeModal === 'email-reconciliation' && (
            <EmailReconciliationModal onClose={handleClose} />
          )}
          {activeModal === 'member-ticket-audit' && (
            <MemberTicketLinkAuditModal onClose={handleClose} onSuccess={handleSuccess} />
          )}
          {activeModal === 'vendor-guest-match' && (
            <VendorGuestMatchModal onClose={handleClose} />
          )}
          {activeModal === 'vendor-staged-edits' && (
            <VendorStagedEditsModal onClose={handleClose} onSuccess={handleSuccess} />
          )}
          {activeModal === 'comm-portal' && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={handleClose}>
              <div style={{ backgroundColor: '#fff', borderRadius: '10px', width: '1300px', maxWidth: '96vw', maxHeight: '92vh', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
                <CommPortalModal onClose={handleClose} />
              </div>
            </div>
          )}
          {activeModal === 'whatsapp-share' && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={handleClose}>
              <div style={{ backgroundColor: '#fff', borderRadius: '12px', width: '500px', maxWidth: '94vw', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '1.4rem' }}>📱</span>
                    <div>
                      <div style={{ color: '#fff', fontWeight: 700, fontSize: '1rem' }}>Share to WhatsApp</div>
                      <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem' }}>Compose a message for the reunion group</div>
                    </div>
                  </div>
                  <button onClick={handleClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: '1.3rem', cursor: 'pointer', padding: '4px 8px', borderRadius: '4px' }} aria-label="Close">✕</button>
                </div>
                {/* Body */}
                <div style={{ padding: '20px' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>Message</label>
                  <textarea
                    value={whatsappMessage}
                    onChange={(e) => setWhatsappMessage(e.target.value)}
                    placeholder="Type your message to the reunion WhatsApp group..."
                    rows={6}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      boxSizing: 'border-box',
                    }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = '#25D366'; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; }}
                    autoFocus
                    aria-label="WhatsApp message"
                  />
                  <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: '6px' }}>
                    This will open WhatsApp with your message pre-filled. Select the group and tap Send.
                  </div>
                  {/* Quick templates */}
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>Quick Templates:</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {[
                        { label: '📢 Reminder', text: '*KDUHUB Communications*\n📢 *Reminder*\n\n' },
                        { label: '🎭 Ball Update', text: '*KDUHUB Communications*\n🎭 *Legacy Ball Update*\n\n' },
                        { label: '📋 Action Required', text: '*KDUHUB Communications*\n📋 *Action Required*\n\n' },
                        { label: '🎉 Announcement', text: '*KDUHUB Communications*\n🎉 *Announcement*\n\n' },
                      ].map(tpl => (
                        <button
                          key={tpl.label}
                          onClick={() => setWhatsappMessage(tpl.text)}
                          style={{
                            padding: '4px 10px',
                            fontSize: '0.72rem',
                            background: '#f3f4f6',
                            border: '1px solid #e5e7eb',
                            borderRadius: '14px',
                            cursor: 'pointer',
                            color: '#374151',
                            fontWeight: 500,
                            transition: 'background 0.15s',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#e5e7eb'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
                          type="button"
                        >
                          {tpl.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                {/* Footer */}
                <div style={{ padding: '14px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: '10px', background: '#f9fafb' }}>
                  <button onClick={handleClose} style={{ padding: '8px 18px', fontSize: '0.85rem', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', color: '#374151', fontWeight: 500 }}>
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (whatsappMessage.trim()) {
                        window.open(`https://wa.me/?text=${encodeURIComponent(whatsappMessage.trim())}`, '_blank');
                        setWhatsappMessage('');
                        handleClose();
                      }
                    }}
                    disabled={!whatsappMessage.trim()}
                    style={{
                      padding: '8px 20px',
                      fontSize: '0.85rem',
                      background: whatsappMessage.trim() ? '#25D366' : '#a7f3d0',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: whatsappMessage.trim() ? 'pointer' : 'not-allowed',
                      color: '#fff',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'background 0.15s',
                    }}
                  >
                    <span>📤</span> Send to WhatsApp
                  </button>
                </div>
              </div>
            </div>
          )}
        </Suspense>
        </ModalErrorBoundary>
      )}

      {/* Success toast */}
      {successMessage && (
        <div
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            backgroundColor: '#2e7d32',
            color: '#fff',
            padding: '12px 20px',
            borderRadius: '8px',
            fontSize: '0.85rem',
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1100,
            maxWidth: '360px',
          }}
          role="status"
          aria-live="polite"
          onClick={() => setSuccessMessage('')}
        >
          ✓ {successMessage}
        </div>
      )}
    </div>
  );
}
